import { Observable, throwError } from 'rxjs';
import { ajax, AjaxError, AjaxRequest, AjaxResponse } from 'rxjs/ajax';
import { catchError, mergeMap, switchMap } from 'rxjs/operators';
import { Cookie } from './cookie';
import { ErrorExtended } from './error-extended';
import { headerConstants, HttpStatusCode } from './http-constants';

/**
 * The Http Retry Handler interface. Describes an object that can handle specific status codes.
 * the handle function takes a status code, AjaxRequest and AjaxError.
 * It returns an observable for new AjaxRequest to try again
 */
export interface HttpResponseRetryHandler {
    canHandle(code: HttpStatusCode, error: AjaxError): boolean;
    handle?(code: HttpStatusCode, request: AjaxRequest, error: AjaxError): Observable<AjaxRequest>;
    handleNoRetry?(code: HttpStatusCode, request: AjaxRequest, error: AjaxError): Observable<void>;
}

/**
 * Retry options for an http request
 */
export class HttpRetryOptions {
    public maxRetry = 0;
    public handlers: HttpResponseRetryHandler[] = [];
}

/**
 * Set of monitors for pre and post process of ajax.
 */
export interface HttpMonitorSet {
    name: string;
    preMonitor: (request: AjaxRequest) => Observable<AjaxRequest | Error>;
    successMonitor: (response: AjaxResponse<any>) => Observable<AjaxResponse<any>>;
    errorMonitor: (error: any) => Observable<AjaxResponse<any>>;
}

/**
 * Enum for http method types
 */
export enum HttpMethod {
    Get = 'GET',
    Post = 'POST',
    Put = 'PUT',
    Delete = 'DELETE',
    Patch = 'PATCH',
    Head = 'HEAD',
    Options = 'OPTIONS'
}

/**
 * The Http observable based class.
 */
export class Http {
    /**
     * Default ajax options must be used for CORS.
     */
    public static defaultHttpOptions: AjaxRequest = <AjaxRequest>{ withCredentials: true, crossDomain: true };

    /**
     * The collection of set of monitors.
     */
    private static monitorSets: HttpMonitorSet[] = [];

    /**
     * The default retry options.
     */
    public defaultRetryOptions: HttpRetryOptions = new HttpRetryOptions();

    /**
     * Register the set of monitors.
     *
     * @param monitorSet The set of monitors.
     */
    public static registerMonitorSet(monitorSet: HttpMonitorSet): void {
        const found = Http.monitorSets.find(monitors => monitors.name === monitorSet.name);
        if (found) {
            return;
        }

        Http.monitorSets.push(monitorSet);
    }

    /**
     * Unregister the set of monitors.
     *
     * @param name The name of set of monitors.
     * @returns boolean true if unregistered the named set.
     */
    public static unregisterMonitors(name: string): boolean {
        const found = Http.monitorSets.find(monitors => monitors.name === name);
        if (found) {
            Http.monitorSets.remove(found);
            return true;
        }

        return false;
    }

    /**
     * The common request method.
     * Adds default responseType, contentType, Accept values if they are not already included in the request
     *
     * @param request the request options.
     * @param options the retry options.
     */
    public request(request: AjaxRequest, retryOptions?: HttpRetryOptions): Observable<AjaxResponse<any>> {
        if (!request) {
            request = <AjaxRequest>{};
        }

        if (!retryOptions) {
            retryOptions = this.defaultRetryOptions;
        }

        if (!request.headers) {
            request.headers = {};
        }

        if (!request.responseType) {
            request.responseType = 'json';
        }

        if (request.headers && !request.headers[headerConstants.CONTENT_TYPE]) {
            (<any>request).headers[headerConstants.CONTENT_TYPE] = 'application/json; charset=utf-8';
        }

        if (request.method === HttpMethod.Get || request.method === HttpMethod.Delete || request.method === HttpMethod.Head) {
            delete (<any>request).headers[headerConstants.CONTENT_TYPE];
            request.body = undefined;
        }

        if (request.headers && !request.headers[headerConstants.ACCEPT]) {
            (<any>request).headers[headerConstants.ACCEPT] = 'application/json, text/plain, */*';
        }

        if (request.headers && !request.headers[headerConstants.CROSS_SITE_REQUEST_FORGERY_TOKEN]) {
            const token = Cookie.getCrossSiteRequestForgeryToken();
            if (token) {
                (<any>request).headers[headerConstants.CROSS_SITE_REQUEST_FORGERY_TOKEN] = token;
            }
        }

        if (retryOptions.maxRetry > 0 && retryOptions.handlers && retryOptions.handlers.length > 0) {
            return this.requestWithHandlers(request, retryOptions);
        }

        return this.monitorAjax(request);
    }

    /**
     * Performs a request without modification.
     * If the result is an error, we will retry with the handlers in options
     *
     * @param request the request options.
     * @param options the retry options.
     * @param count the current iteration of the retry cycle.
     */
    private requestWithHandlers(request: AjaxRequest, retryOptions: HttpRetryOptions, count: number = 0): Observable<AjaxResponse<any>> {
        return this.monitorAjax(request)
            .pipe(
                catchError((error: AjaxError, caught: any) => {
                    // original request is replaced with latest instance. it must take current error request.
                    const caughtRequest = caught.source.request || (caught.source.source && caught.source.source.value) || request;
                    if (++count > retryOptions.maxRetry) {
                        return throwError(() => error);
                    }

                    const handler = retryOptions.handlers.find(handler2 => handler2.canHandle(error.status, error));
                    if (handler) {
                        if (handler.handleNoRetry) {
                            return handler.handleNoRetry(error.status, caughtRequest, error)
                                .pipe(mergeMap(() => throwError(() => error)));
                        } else if (handler.handle) {
                            return handler.handle(error.status, caughtRequest, error)
                                .pipe(
                                    catchError(handlerError => {
                                        // if the handler throws, return the original error with an inserted
                                        // property for the handler error
                                        error['handlerError'] = handlerError;
                                        return throwError(() => error);
                                    }),
                                    switchMap(() => this.monitorAjax(caughtRequest)));
                        }
                    }

                    return throwError(() => error);
                }));
    }

    /**
     * Performs a request with `get` http method.
     *
     * @param url the url.
     * @param request the request options.
     * @param options the retry options.
     */
    public get(url: string, request?: AjaxRequest, options?: HttpRetryOptions): Observable<AjaxResponse<any>> {
        request = request ? request : <AjaxRequest>{};
        request.url = url;
        request.method = HttpMethod.Get;
        return this.request(request, options);
    }

    /**
     * Performs a request with `post` http method.
     *
     * @param url the url.
     * @param body the body content.
     * @param request the request options.
     * @param options the retry options.
     */
    public post(url: string, body: any, request?: AjaxRequest, options?: HttpRetryOptions): Observable<AjaxResponse<any>> {
        request = request ? request : <AjaxRequest>{};
        request.url = url;
        request.method = HttpMethod.Post;
        request.body = body;
        return this.request(request, options);
    }

    /**
     * Performs a request with `put` http method.
     *
     * @param url the url.
     * @param body the body content.
     * @param request the request options.
     * @param options the retry options.
     */
    public put(url: string, body: any, request?: AjaxRequest, options?: HttpRetryOptions): Observable<AjaxResponse<any>> {
        request = request ? request : <AjaxRequest>{};
        request.url = url;
        request.method = HttpMethod.Put;
        request.body = body;
        return this.request(request, options);
    }

    /**
     * Performs a request with `delete` http method.
     *
     * @param url the url.
     * @param request the request options.
     * @param options the retry options.
     */
    public delete(url: string, request?: AjaxRequest, options?: HttpRetryOptions): Observable<AjaxResponse<any>> {
        request = request ? request : <AjaxRequest>{};
        request.url = url;
        request.method = HttpMethod.Delete;
        return this.request(request, options);
    }

    /**
     * Performs a request with `patch` http method.
     *
     * @param url the url.
     * @param body the body content.
     * @param request the request options.
     * @param options the retry options.
     */
    public patch(url: string, body: any, request?: AjaxRequest, options?: HttpRetryOptions): Observable<AjaxResponse<any>> {
        request = request ? request : <AjaxRequest>{};
        request.url = url;
        request.method = HttpMethod.Patch;
        request.body = body;
        return this.request(request, options);
    }

    /**
     * Performs a request with `head` http method.
     *
     * @param url the url.
     * @param request the request options.
     * @param options the retry options.
     */
    public head(url: string, request?: AjaxRequest, options?: HttpRetryOptions): Observable<AjaxResponse<any>> {
        request = request ? request : <AjaxRequest>{};
        request.url = url;
        request.method = HttpMethod.Head;
        return this.request(request, options);
    }

    /**
     * Performs a request with `options` http method.
     *
     * @param url the url.
     * @param request the request options.
     * @param options the retry options.
     */
    public options(url: string, request?: AjaxRequest, options?: HttpRetryOptions): Observable<AjaxResponse<any>> {
        request = request ? request : <AjaxRequest>{};
        request.url = url;
        request.method = HttpMethod.Options;
        return this.request(request, options);
    }

    /**
     * Performs a request with 'get' http method with cache control.
     *
     * @param url the uri for GET call.
     * @return the observable for GET result data.
     */
    public getNoCache(
        url: string, noCache = true, responseType: XMLHttpRequestResponseType = '', withCredentials = true): Observable<AjaxResponse<any>> {
        const publish = new Observable<AjaxResponse<any>>(observer => {
            const request = new XMLHttpRequest();
            const handler = () => {
                if (request.readyState === XMLHttpRequest.DONE) {
                    if (request.status === 200) {
                        try {
                            let response: string;
                            if (responseType === '') {
                                response = JSON.parse(request.response);
                            } else {
                                response = request.response;
                            }

                            observer.next(<AjaxResponse<any>>{
                                status: request.status,
                                response
                            });
                            observer.complete();
                        } catch (e) {
                            observer.error(e);
                        }
                    } else {
                        const error = new ErrorExtended<{ status: number, url: string }>(request.statusText);
                        error.extendedSource = ErrorExtended.sources.getNoCache;
                        error.extended = { status: request.status, url };
                        observer.error(error);
                    }
                }
            };

            request.open('Get', url);
            request.withCredentials = withCredentials;
            request.responseType = responseType;
            request.setRequestHeader(headerConstants.ACCEPT, 'application/json, text/plain, */*');

            if (noCache) {
                request.setRequestHeader('Cache-control', 'no-cache');
            }

            request.onreadystatechange = handler;
            request.send();
        });

        return publish;
    }

    /**
     * Performs a request with 'delete' http method without waiting for the response.
     *
     * @param url the uri for GET call.
     */
    public deleteQuick(url: string, headers: any): void {
        const request = new XMLHttpRequest();
        request.open('Delete', url);
        request.withCredentials = true;
        request.responseType = 'json';
        request.setRequestHeader(headerConstants.ACCEPT, 'application/json, text/plain, */*');
        request.setRequestHeader(headerConstants.CONTENT_TYPE, 'application/json; charset=utf-8');
        request.setRequestHeader(headerConstants.CACHE_CONTROL, 'no-cache');

        let xsrf = false;
        if (headers) {
            for (const key in headers) {
                if (key) {
                    request.setRequestHeader(key, headers[key]);
                    if (key.toLowerCase() === headerConstants.CROSS_SITE_REQUEST_FORGERY_TOKEN.toLowerCase()) {
                        xsrf = true;
                    }
                }
            }
        }

        const token = Cookie.getCrossSiteRequestForgeryToken();
        if (token && !xsrf) {
            request.setRequestHeader(headerConstants.CROSS_SITE_REQUEST_FORGERY_TOKEN, token);
        }

        // not watching any response.
        request.send();
    }

    private monitorAjax(request: AjaxRequest): Observable<AjaxResponse<any>> {
        let monitored: (request: AjaxRequest) => Observable<AjaxResponse<any>> = ajax;
        for (const monitorSet of Http.monitorSets) {
            monitored = this.monitor(monitored, monitorSet);
        }

        return monitored(request);
    }

    private monitor(target: (request: AjaxRequest) => Observable<AjaxResponse<any>>, monitorSet: HttpMonitorSet): (request: AjaxRequest) => Observable<AjaxResponse<any>> {

        return function (request) {
            return monitorSet.preMonitor(request)
                .pipe(switchMap(monitoredRequest => {
                    if (monitoredRequest instanceof Error) {
                        return throwError(() => monitoredRequest);
                    }

                    return target(monitoredRequest).pipe(
                        catchError((error: AjaxError) => {
                            return monitorSet.errorMonitor(error);
                        }),
                        switchMap(response => monitorSet.successMonitor(response))
                    );
                }));
        };
    }
}
